// eslint-disable-next-line
import {
  IsBoolean,
  IsDefined,
  IsIBAN,
  IsInt,
  IsNotEmpty,
  IsPositive,
  IsString,
  IsUUID,
  IsOptional,
  Length,
  Matches,
  Max,
  MaxLength,
  Min,
  ValidateNested, IsDate,
} from 'class-validator';
import { Exclude, Expose, Type } from 'class-transformer';
import repository from '@/api/repository';
import { PAYMENT_ORDERS } from '@/constants/repository';
import generateId from '@/utils/random';
import { validateSyncOrThrow } from '@/utils/validations';

export class DirectDebitMandate {
  @IsDefined()
  @IsString()
  @IsNotEmpty()
  @MaxLength(35)
  @Expose()
  mandateId!: string;

  @IsDefined()
  @Type(() => Date)
  @IsDate()
  @Expose()
  dateOfSignature!: Date;

  get isEmpty(): boolean {
    return !this.mandateId && !this.dateOfSignature;
  }

  static from({ mandateId, dateOfSignature }: Pick<DirectDebitMandate, 'mandateId' | 'dateOfSignature'>) {
    const mandate = new DirectDebitMandate();

    mandate.mandateId = mandateId;
    mandate.dateOfSignature = dateOfSignature;

    validateSyncOrThrow(mandate);

    return mandate;
  }
}

export class BankAccount {
  @IsUUID()
  id!: string;

  @IsBoolean()
  isPayable!: boolean;

  @IsDefined()
  @IsNotEmpty()
  @IsString()
  @MaxLength(70)
  @Expose()
  name!: string;

  @IsOptional()
  @IsDefined()
  @IsNotEmpty()
  @IsString()
  @IsIBAN()
  @Expose()
  iban!: string | null;

  @IsOptional()
  @IsDefined()
  @IsNotEmpty()
  @IsString()
  @MaxLength(7)
  @Expose()
  synesisCode!: string | null;

  @IsOptional()
  @IsDefined()
  @IsNotEmpty()
  @IsString()
  @Expose()
  creditorSchemeId!: string | null;

  @IsOptional()
  @ValidateNested()
  @Type(() => DirectDebitMandate)
  @Expose()
  directDebitMandate!: DirectDebitMandate | null;

  static default(): BankAccount {
    const bankAccount = new BankAccount();
    bankAccount.name = '';
    bankAccount.iban = null;
    bankAccount.synesisCode = null;
    bankAccount.isPayable = false;
    bankAccount.directDebitMandate = new DirectDebitMandate();

    return bankAccount;
  }
}

export class Transaction {
  @IsDefined()
  @IsUUID()
  @Exclude()
  id!: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => BankAccount)
  @Expose()
  creditor!: BankAccount;

  @IsDefined()
  @IsInt()
  @IsPositive()
  @Min(1)
  @Max(99999999999999)
  @Expose()
  amount!: number;

  @IsDefined()
  @IsString()
  @IsNotEmpty()
  @Length(4)
  @Matches(/^HR\d{2}$/)
  @Exclude()
  model!: string;

  @IsDefined()
  @IsString()
  @Length(0, 31)
  @Exclude()
  // poziv na broj primatelja
  beneficiaryRef!: string;

  @IsDefined()
  @IsString()
  @IsNotEmpty()
  @MaxLength(140)
  @Expose()
  additionalInfo!: string;

  @IsBoolean()
  @Exclude()
  isScanned!: boolean;

  @Expose({ toPlainOnly: true })
  get ref() {
    return `${this.model}${this.beneficiaryRef}`;
  }

  static from(creditor: BankAccount, amount: number, {
    model = 'HR99', beneficiaryRef = '', additionalInfo = 'Račun', isScanned = false,
  } = {}) {
    const transaction = new Transaction();

    transaction.id = generateId();
    transaction.creditor = creditor;
    transaction.amount = amount;
    transaction.model = model;
    transaction.beneficiaryRef = beneficiaryRef;
    transaction.additionalInfo = additionalInfo;
    transaction.isScanned = isScanned;

    validateSyncOrThrow(transaction);

    return transaction;
  }
}

export class TransactionGroup {
  @IsDefined()
  @IsUUID()
  @Exclude()
  id!: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => BankAccount)
  @Expose()
  debtor!: BankAccount;

  @IsDefined()
  // TODO: lets comment this out until new feature gets merged that enables simpler validations
  // @ArrayNotEmpty({ groups: ['api'] })
  @ValidateNested()
  @Type(() => Transaction)
  @Expose()
  transactions!: Transaction[];

  static from(debtor: BankAccount) {
    const transactionGroup = new TransactionGroup();

    transactionGroup.id = generateId();
    transactionGroup.debtor = debtor;
    transactionGroup.transactions = [];

    validateSyncOrThrow(transactionGroup, { forbidUnknownValues: true });

    return transactionGroup;
  }
}

export class PaymentOrder {
  @IsDefined()
  @IsNotEmpty()
  @IsString()
  @MaxLength(70)
  @Expose()
  initiatingPartyName!: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => TransactionGroup)
  @Expose()
  transactionGroups!: TransactionGroup[];

  static from(initiatingPartyName: string) {
    const paymentOrder = new PaymentOrder();

    paymentOrder.initiatingPartyName = initiatingPartyName;
    paymentOrder.transactionGroups = [];

    validateSyncOrThrow(paymentOrder, { forbidUnknownValues: true });

    return paymentOrder;
  }
}

export default {
  post(paymentOrder: PaymentOrder) {
    return repository.post<{filename: string; content: string}>(PAYMENT_ORDERS, paymentOrder);
  },
};
